import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TABS } from './tabs/index.js';
import { logInfo } from '../../../../../utils/logger';
import { useSurvey } from '../../../../../contexts/SurveyContext';
import { usePOIManager } from '../../../../../hooks/map/usePOIManager';
import { LoadingSpinner } from '../../../../../components/common/LoadingSpinner';
import { useTeam } from '../../../../../contexts/TeamContext';

export const MapSidebar = ({ map, onVisibilityChange, visibilityState, className }) => {
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState('info');
  const [width, setWidth] = useState(400);
  const { teamMembers } = useTeam();
  const { activeSurvey } = useSurvey();
  const { pois, routes, loading: dataLoading } = usePOIManager(activeSurvey?.id);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsCollapsed(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleVisibilityToggle = useCallback((type, visible) => {
    onVisibilityChange?.(type, visible);
    logInfo(`Visibility toggled for ${type}`);
  }, [onVisibilityChange]);

  const ActiveComponent = TABS.find(tab => tab.id === activeTab)?.component;

  // Log data availability
  useEffect(() => {
    console.log('[MapSidebar] Data status:', {
      activeSurvey: !!activeSurvey,
      poisCount: pois?.length,
      routesCount: routes?.length,
      loading: dataLoading
    });
  }, [activeSurvey, pois, routes, dataLoading]);

  return (
    <div 
      data-sidebar
      className={`fixed top-[4.5rem] right-0 bottom-0 bg-[#2A2B2E] shadow-lg z-[150] flex flex-col transition-all duration-300 ${
        isCollapsed ? 'translate-x-[calc(100%-64px)]' : 'translate-x-0'
      }`}
      style={{ 
        width: isCollapsed ? '400px' : `${width}px`
      }}
    >
      {/* Only show collapse toggle button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 p-2 bg-[#2A2B2E] rounded-l-lg text-white hover:bg-gray-700 transition-colors z-[151]"
      >
        {isCollapsed ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
      </button>

      {/* Only show content when not collapsed */}
      {!isCollapsed && (
        <>
          {/* Tabs */}
          <div className="flex border-b border-gray-700">
            {TABS.map(({ id, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex-1 p-4 ${
                  activeTab === id 
                    ? 'text-red-500 border-b-2 border-red-500' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon className="w-5 h-5 mx-auto" />
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain relative">
            {ActiveComponent && (
              <div className="h-full relative">
                {dataLoading ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <ActiveComponent 
                    map={map}
                    team={teamMembers}
                    onVisibilityChange={handleVisibilityToggle}
                    visibilityState={visibilityState}
                    pois={pois}
                    routes={routes}
                    activeSurvey={activeSurvey}
                  />
                )}
              </div>
            )}
          </div>

          {/* Resize Handle */}
          <div 
            className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-red-500/20 transition-colors" 
            onMouseDown={(e) => {
              e.preventDefault();
              const sidebar = e.currentTarget.closest('[data-sidebar]');
              if (!sidebar) return;
              
              const startX = e.clientX;
              const startWidth = sidebar.offsetWidth;
              
              const onMouseMove = (e) => {
                e.preventDefault();
                const width = startWidth - (e.clientX - startX);
                if (sidebar) {
                  const newWidth = Math.min(Math.max(width, 400), 900);
                  sidebar.style.width = `${newWidth}px`;
                  setWidth(newWidth);
                }
              };
              
              const onMouseUp = () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
              };
              
              document.addEventListener('mousemove', onMouseMove);
              document.addEventListener('mouseup', onMouseUp);
            }}
          />
        </>
      )}
    </div>
  );
};

export default MapSidebar;